import { useEffect, useState } from 'react';
import { createPresentation, createSupplier, getSuppliers, updatePresentation } from '../api/inventory';
import { getErrorMessage } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { canProposeSupplier } from '../auth/permissions';
import { compatibleUnitOptions } from '../utils/units';
import { formatMoney, formatUnitCost } from '../utils/costFormatting';
import { ProgressiveSection } from './ui/ProgressiveSection';
import { unitWordPlural, unitWordSingular } from '../utils/unitWording';
import { selectableSupplierOptions } from '../utils/supplierOptions';
import { Field, SelectField, SimpleEditor } from './InventoryEditorKit';
import { Modal } from './Modal';
import type { Ingredient, InventoryUnit, PurchasePresentation, Supplier } from '../types/inventory';

// Editor de presentación de compra ("precio de compra" en idioma de mostrador). Vive fuera de
// InventoryPage para poder abrirse EN CONTEXTO desde el Descuento por ventas (acción "Capturar
// precio de compra") sin import circular página↔panel.
//
// Ola 3: el proveedor ya no es texto libre — se elige de los proveedores del negocio (inicia
// vacío) y, si falta, se agrega/propone AQUÍ MISMO (reutiliza el mecanismo 26A: el backend decide
// ACTIVE o PENDING_APPROVAL según el rol) sin cerrar el formulario ni perder lo capturado.
export function PresentationEditor({ value, ingredients, units, initialIngredientId, defaultCostingDefault = false, onClose, onSaved }: { value?: PurchasePresentation; ingredients: Ingredient[]; units: InventoryUnit[]; initialIngredientId?: string; defaultCostingDefault?: boolean; onClose: () => void; onSaved: () => Promise<void> }) {
  const { user } = useAuth();
  // P-075: sin insumo indicado se preseleccionaba ingredients[0] aunque estuviera INACTIVO; el select solo
  // lista activos, así que se veía un insumo y se guardaba en otro. Ahora cae al primer activo, y el insumo
  // ya elegido (el de la presentación que se edita, o el del detalle desde donde se abrió) siempre se lista.
  const [form, setForm] = useState(() => { const defaultIngredient = ingredients.find((ingredient) => ingredient.id === (value?.ingredientId || initialIngredientId)) || ingredients.find((ingredient) => ingredient.isActive); return { ingredientId: defaultIngredient?.id || '', name: value?.name || '', quantity: value?.quantity || '', unitId: value?.unit.id || defaultIngredient?.baseUnit.id || '', cost: value?.cost || '', supplierName: value?.supplierName || '', isCostingDefault: value?.isCostingDefault || defaultCostingDefault }; });
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [supplierModalOpen, setSupplierModalOpen] = useState(false);
  const [supplierNotice, setSupplierNotice] = useState<string>();
  const selectedIngredient = ingredients.find((ingredient) => ingredient.id === form.ingredientId);
  const selectIngredient = (ingredientId: string) => { const ingredient = ingredients.find((item) => item.id === ingredientId); setForm({ ...form, ingredientId, unitId: ingredient?.baseUnit.id || form.unitId }); };

  useEffect(() => {
    let alive = true;
    getSuppliers().then((result) => { if (alive) setSuppliers(result.suppliers); }).catch(() => { /* sin lista, el select queda con la opción vacía */ });
    return () => { alive = false; };
  }, []);

  const supplierOptions = selectableSupplierOptions(suppliers, form.supplierName);
  const proposeOnly = !canProposeSupplier(user?.role) ? false : user?.role === 'EMPLOYEE';

  // La unidad elegida da nombre a la etiqueta del contenido, y la division da el costo unitario.
  // Ninguno de los dos se guarda: la base guarda `cost` (la presentacion completa) y `quantity`.
  const unidadElegida = units.find((unit) => unit.id === form.unitId);
  const unidadTexto = unitWordPlural(unidadElegida);
  const unidadSingular = unitWordSingular(unidadElegida);
  const unidadSimbolo = unidadElegida?.symbol ?? '';
  const contenido = Number(form.quantity);
  const pagado = Number(form.cost);
  const costoUnitario = contenido > 0 && Number.isFinite(pagado) && pagado >= 0 ? pagado / contenido : null;

  // ── El asistente (mismo mecanismo que PresentationQuickCreateModal) ────────────────────────
  type Paso = 'name' | 'unit' | 'quantity' | 'cost';
  const ORDEN: Paso[] = ['name', 'unit', 'quantity', 'cost'];
  const nombre = form.name.trim();
  const nombreTexto = nombre || 'esta presentación';
  // P-079 (Codex R1 · K10): «lleno» no es «válido»: un costo negativo o un contenido en cero no se ponen en verde.
  const costoValido = form.cost !== '' && Number.isFinite(pagado) && pagado >= 0;
  const lleno: Record<Paso, boolean> = { name: Boolean(nombre), unit: Boolean(unidadElegida), quantity: contenido > 0, cost: costoValido };
  const errores: Partial<Record<Paso, string>> = {
    quantity: form.quantity !== '' && !(contenido > 0) ? 'El contenido debe ser mayor que cero.' : undefined,
    cost: form.cost !== '' && !costoValido ? 'El costo no puede ser negativo.' : undefined,
  };
  // Al crear abre por el nombre; al editar todo llega lleno y abre con todo contestado.
  const [open, setOpen] = useState<Paso | null>(() => (value ? null : 'name'));
  // Codex R2 · R2: el paso siguiente toma el foco solo al avanzar a propósito o al salir del campo sin ir a otro control.
  const [focusNext, setFocusNext] = useState(true);
  // Codex R2 · R3: mientras se guarda, las secciones se quedan como están (SimpleEditor lleva su propio estado).
  const [saving, setSaving] = useState(false);
  const avanzar = (desde: Paso, conFoco = true) => {
    if (!lleno[desde]) return;
    const siguiente = ORDEN.slice(ORDEN.indexOf(desde) + 1).find((p) => !lleno[p]) ?? ORDEN.find((p) => !lleno[p]) ?? null;
    setFocusNext(conFoco);
    setOpen(siguiente);
  };
  const salir = (desde: Paso) => (event: React.FocusEvent<HTMLInputElement>) => avanzar(desde, !event.relatedTarget);
  const enterAvanza = (desde: Paso) => (event: React.KeyboardEvent<HTMLInputElement>) => { if (event.key === 'Enter') { event.preventDefault(); avanzar(desde); } };
  // P-079: cada paso es una sección progresiva (la pieza común), como en el modal rápido.
  const paso = (key: Paso, label: string, valor: string, pendiente: boolean, abierto: React.ReactNode) => (
    <ProgressiveSection busy={saving} complete={lleno[key]} error={errores[key]} key={key} label={label} onOpen={() => setOpen(key)} open={open === key} summary={valor} tone={pendiente ? 'missing' : 'done'}>
      {abierto}
    </ProgressiveSection>
  );

  return <>
    <SimpleEditor title={value ? 'Editar presentación' : 'Crear presentación'} onClose={onClose} onSaved={onSaved} canSubmit={ORDEN.every((p) => lleno[p])} submit={async () => { const faltante = ORDEN.find((p) => !lleno[p]); if (faltante) { setOpen(faltante); throw new Error('Falta un paso por contestar: completa el que quedo abierto.'); } const input = { name: form.name.trim(), quantity: Number(form.quantity), unitId: form.unitId, cost: Number(form.cost), currency: 'MXN', supplierName: form.supplierName.trim() || null, isCostingDefault: form.isCostingDefault }; setSaving(true); try { if (value) await updatePresentation(value.id, input); else await createPresentation(form.ingredientId, input); } finally { setSaving(false); } }}>
      <div className="form-grid form-grid--compact">
        <SelectField required label="Insumo" disabled={Boolean(value)} value={form.ingredientId} onChange={selectIngredient} options={ingredients.filter((ingredient) => ingredient.isActive || ingredient.id === form.ingredientId).map((ingredient) => [ingredient.id, ingredient.name])} />
        {/* Formulario GUIADO, igual que el modal rápido de Compras: un paso abierto, lo contestado
            se encoge a una línea con «Cambiar». Al EDITAR todo llega lleno, así que abre con los
            cuatro contestados y cualquiera se reabre con «Cambiar». */}
        <div className="wiz-steps form-grid__full">
          {paso('name', 'Nombre', nombre || 'Pendiente', !nombre,
            <>
              <h3 className="wiz-question">¿Cómo se llama esta presentación?</h3>
              <div className="wiz-answers"><Field required label="" ariaLabel="Nombre de la presentación" autoFocus={focusNext} placeholder="Ej.: Caja de 9 bolsas de 2 litros cada una" value={form.name} onChange={(name) => setForm({ ...form, name })} onBlur={salir('name')} onKeyDown={enterAvanza('name')} /></div>
            </>)}
          {paso('unit', 'Unidad', unidadElegida ? `${unidadElegida.name} (${unidadElegida.symbol})` : 'Pendiente', !unidadElegida,
            <>
              <h3 className="wiz-question">¿En qué unidad viene {nombreTexto}?</h3>
              <div className="wiz-answers"><SelectField required label="" ariaLabel="Unidad del contenido" autoFocus={focusNext} value={form.unitId} onChange={(unitId) => { setForm({ ...form, unitId }); avanzar('unit'); }} options={compatibleUnitOptions(units, selectedIngredient?.baseUnit, form.unitId)} /></div>
            </>)}
          {paso('quantity', 'Contenido', contenido > 0 ? `${contenido.toLocaleString('es-MX')} ${unidadTexto}` : 'Pendiente', !(contenido > 0),
            <>
              <h3 className="wiz-question">¿Cuántos {unidadTexto} trae {nombreTexto}?</h3>
              <div className="wiz-answers"><Field required label="" ariaLabel={`Contenido en ${unidadTexto}`} autoFocus={focusNext} hint="El total que trae la presentación completa (ej. 18 si son 9 bolsas de 2 litros) — no cuántas compras." type="number" value={form.quantity} onChange={(quantity) => setForm({ ...form, quantity })} onBlur={salir('quantity')} onKeyDown={enterAvanza('quantity')} /></div>
            </>)}
          {paso('cost', 'Costo aproximado', form.cost !== '' && Number.isFinite(pagado) ? formatMoney(String(pagado)) : 'Pendiente', form.cost === '',
            <>
              <h3 className="wiz-question">Costo aproximado de {nombreTexto}</h3>
              <div className="wiz-answers"><Field required label="" ariaLabel="Costo aproximado de la presentación" autoFocus={focusNext} hint="Lo que pagas por la presentación completa. Puede cambiar en cada compra." type="number" value={form.cost} onChange={(cost) => setForm({ ...form, cost })} onBlur={salir('cost')} onKeyDown={enterAvanza('cost')} /></div>
            </>)}
        </div>
        {/* La división, hecha: siempre visible, se actualiza sola. */}
        <p className={costoUnitario === null ? 'presentation-unit-cost presentation-unit-cost--pending form-grid__full' : 'presentation-unit-cost form-grid__full'}>
          {costoUnitario === null
            ? <>Escribe el contenido y el costo y aquí verás cuánto sale cada {unidadSingular}.</>
            : <>Sale a <strong>{formatUnitCost(costoUnitario)} por {unidadSimbolo}</strong> <span>· {formatUnitCost(pagado)} entre {contenido.toLocaleString('es-MX')} {unidadSimbolo}</span></>}
        </p>
        <label className="field">
          <span>Proveedor</span>
          <select onChange={(event) => { setForm({ ...form, supplierName: event.target.value }); setSupplierNotice(undefined); }} value={form.supplierName}>
            <option value="">Selecciona un proveedor (opcional)</option>
            {supplierOptions.map(([name, label]) => <option key={name} value={name}>{label}</option>)}
          </select>
          <button className="link-button presentation-add-supplier" onClick={() => setSupplierModalOpen(true)} type="button">
            {proposeOnly ? '+ Proponer proveedor' : '+ Agregar proveedor'}
          </button>
          {supplierNotice && <small className="cost-mode-hint">{supplierNotice}</small>}
        </label>
        <label className="costing-default-option form-grid__full"><input checked={form.isCostingDefault} onChange={(event) => setForm({ ...form, isCostingDefault: event.target.checked })} type="checkbox" /><span><strong>Usar esta presentación como referencia de costo</strong><small>La usaremos como referencia para calcular el costo estimado de tus recetas.</small></span></label>
      </div>
    </SimpleEditor>
    {supplierModalOpen && (
      <InlineSupplierModal
        proposeOnly={proposeOnly}
        onClose={() => setSupplierModalOpen(false)}
        onCreated={(supplier) => {
          // Regreso automático al formulario, con TODO lo capturado intacto (solo cambia el proveedor).
          setSuppliers((current) => [...current, supplier]);
          setForm((current) => ({ ...current, supplierName: supplier.name }));
          setSupplierNotice(supplier.approvalStatus === 'PENDING_APPROVAL'
            ? 'Proveedor propuesto — pendiente de aprobación por tu dueño o gerente.'
            : undefined);
          setSupplierModalOpen(false);
        }}
      />
    )}
  </>;
}

/** Alta/propuesta de proveedor SIN salir del formulario padre (mecanismo 26A: el backend decide el
 *  estado). Reutilizado por Crear presentación y por la captura de ticket. */
export function InlineSupplierModal({ proposeOnly, onClose, onCreated }: { proposeOnly: boolean; onClose: () => void; onCreated: (supplier: Supplier) => void }) {
  const [name, setName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string>();

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed || isSaving) return;
    setIsSaving(true);
    setError(undefined);
    try {
      const { supplier } = await createSupplier({ name: trimmed, contactName: null, phone: null, email: null, notes: null });
      onCreated(supplier);
    } catch (saveError) {
      setError(getErrorMessage(saveError, proposeOnly ? 'No fue posible enviar la propuesta.' : 'No fue posible guardar el proveedor.'));
      setIsSaving(false);
    }
  };

  return (
    <Modal
      title={proposeOnly ? 'Proponer proveedor' : 'Agregar proveedor'}
      description={proposeOnly ? 'Tu propuesta se enviará a aprobación; mientras tanto puedes dejarla seleccionada en la presentación.' : 'Se agrega al catálogo de proveedores de tu negocio y queda seleccionado en la presentación.'}
      isBusy={isSaving}
      onClose={onClose}
      footer={<>
        <button className="button button--ghost" disabled={isSaving} onClick={onClose} type="button">Cancelar</button>
        <button className="button button--primary" disabled={isSaving || !name.trim()} onClick={() => void save()} type="button">{isSaving ? 'Guardando...' : proposeOnly ? 'Enviar propuesta' : 'Guardar proveedor'}</button>
      </>}
    >
      {error && <div className="alert alert--error">{error}</div>}
      <label className="field"><span>Nombre del proveedor</span><input autoFocus onChange={(event) => setName(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); void save(); } }} placeholder="Ej. Abarrotes La Central" value={name} /></label>
    </Modal>
  );
}
